export interface IndustryDetail {
  slug: string;
  name: string;
  tagline: string;
  desc: string;
  image: string;
  heroImage: string;
  icon: string;
  color: string;
  challenges: string[];
  solutions: { title: string; desc: string; icon: string }[];
  features: { title: string; desc: string; icon: string }[];
  techStack: { name: string; logo?: string; icon?: string }[];
  benefits: { title: string; desc: string; icon: string }[];
  faq: { q: string; a: string }[];
  caseStudy: {
    title: string;
    desc: string;
    tech: string[];
    results: string;
  };
}

export const industriesList = [
  {
    slug: "healthcare",
    name: "Healthcare",
    tagline: "Digital solutions for modern healthcare businesses.",
    desc: "HIPAA-compliant patient management systems, telehealth platforms and healthcare SaaS solutions built for security and compliance.",
    image: "https://readdy.ai/api/search-image?query=Modern%20healthcare%20technology%20platform%2C%20medical%20dashboard%20with%20patient%20data%2C%20clean%20UI%20interface%2C%20blue%20and%20white%20color%20scheme%2C%20professional%20healthcare%20software%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-healthcare&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Modern%20healthcare%20technology%20concept%2C%20digital%20health%20platform%20with%20medical%20data%20visualization%2C%20futuristic%20medical%20software%20interface%2C%20blue%20and%20teal%20gradient%20lighting%2C%20professional%20healthcare%20tech%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-healthcare&orientation=landscape",
    icon: "ri-hospital-line",
    color: "#0ea5e9",
    challenges: [
      "Data security and HIPAA compliance",
      "Patient data management at scale",
      "Integration with existing hospital systems",
      "Real-time monitoring and alerts",
      "Multi-device accessibility",
    ],
    solutions: [
      { title: "Patient Portals", desc: "Secure patient management and appointment scheduling.", icon: "ri-user-line" },
      { title: "Telehealth Platforms", desc: "Remote consultation and video appointment systems.", icon: "ri-video-line" },
      { title: "Healthcare SaaS", desc: "Multi-tenant platforms for healthcare providers.", icon: "ri-cloud-line" },
      { title: "Medical APIs", desc: "Integration with EHR, lab systems and insurance.", icon: "ri-exchange-line" },
    ],
    features: [
      { title: "HIPAA Compliance", desc: "End-to-end encryption, audit logs and compliance controls.", icon: "ri-shield-check-line" },
      { title: "Patient Records", desc: "Secure electronic health records with role-based access.", icon: "ri-file-list-line" },
      { title: "Appointment System", desc: "Online booking, reminders and calendar integration.", icon: "ri-calendar-line" },
      { title: "Real-time Analytics", desc: "Dashboards for operational and clinical insights.", icon: "ri-bar-chart-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
    ],
    benefits: [
      { title: "Better Patient Care", desc: "Streamlined workflows improve patient outcomes.", icon: "ri-heart-pulse-line" },
      { title: "Reduced Costs", desc: "Automation reduces administrative overhead.", icon: "ri-money-cny-circle-line" },
      { title: "Data Security", desc: "HIPAA-compliant infrastructure and practices.", icon: "ri-lock-2-line" },
      { title: "Scalability", desc: "Grows with your practice or hospital network.", icon: "ri-expand-diagonal-line" },
    ],
    faq: [
      { q: "Is your healthcare software HIPAA compliant?", a: "Yes. We follow HIPAA guidelines including encryption, audit logs, access controls and regular security assessments." },
      { q: "Can you integrate with existing hospital systems?", a: "Yes. We build APIs that integrate with EHR systems, lab software, billing platforms and insurance portals." },
      { q: "How do you ensure patient data security?", a: "We implement end-to-end encryption, role-based access, secure APIs, regular penetration testing and SOC 2 compliant infrastructure." },
    ],
    caseStudy: {
      title: "Healthcare Management Platform",
      desc: "Built a HIPAA-compliant patient management system with mobile companion app for a growing healthcare network.",
      tech: ["Laravel", "React", "PostgreSQL", "AWS", "Redis"],
      results: "40% reduction in admin time, 99.9% uptime",
    },
  },
  {
    slug: "fintech",
    name: "FinTech",
    tagline: "Secure and scalable financial technology solutions.",
    desc: "Custom financial platforms, payment integrations and secure transaction systems built for modern banking and fintech needs.",
    image: "https://readdy.ai/api/search-image?query=Modern%20financial%20technology%20platform%2C%20fintech%20dashboard%20with%20transaction%20charts%20and%20graphs%2C%20secure%20banking%20interface%2C%20dark%20blue%20and%20purple%20gradient%20lighting%2C%20professional%20financial%20software%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-fintech&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20fintech%20visualization%2C%20financial%20data%20streams%2C%20secure%20transaction%20network%2C%20blue%20and%20purple%20gradient%20lighting%20with%20gold%20accents%2C%20digital%20currency%20concept%2C%20premium%20technology%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-fintech&orientation=landscape",
    icon: "ri-bank-line",
    color: "#8b5cf6",
    challenges: [
      "Secure transaction processing",
      "PCI DSS compliance",
      "Real-time fraud detection",
      "Multi-currency support",
      "Integration with banking APIs",
    ],
    solutions: [
      { title: "Payment Platforms", desc: "Custom payment processing with Stripe, PayPal and bank APIs.", icon: "ri-bank-card-line" },
      { title: "Financial Dashboards", desc: "Real-time analytics for transactions, revenue and users.", icon: "ri-bar-chart-box-line" },
      { title: "Secure APIs", desc: "Bank-grade API security with OAuth and encryption.", icon: "ri-shield-check-line" },
      { title: "Wallet Systems", desc: "Digital wallet and account management platforms.", icon: "ri-wallet-3-line" },
    ],
    features: [
      { title: "PCI DSS Compliance", desc: "Built-in compliance for secure payment processing.", icon: "ri-shield-check-line" },
      { title: "Real-time Processing", desc: "Instant transaction processing and notification systems.", icon: "ri-flashlight-line" },
      { title: "Multi-currency", desc: "Support for multiple currencies and exchange rates.", icon: "ri-coins-line" },
      { title: "Fraud Detection", desc: "AI-powered fraud detection and risk scoring.", icon: "ri-spy-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
    ],
    benefits: [
      { title: "Faster Transactions", desc: "Real-time processing reduces payment delays.", icon: "ri-flashlight-line" },
      { title: "Better Security", desc: "Bank-grade security and compliance standards.", icon: "ri-shield-check-line" },
      { title: "User Experience", desc: "Smooth, intuitive financial interfaces.", icon: "ri-user-smile-line" },
      { title: "Global Reach", desc: "Multi-currency and international payment support.", icon: "ri-global-line" },
    ],
    faq: [
      { q: "Is your fintech software PCI DSS compliant?", a: "Yes. We follow PCI DSS standards for payment processing including secure card storage, encrypted transmission and regular security audits." },
      { q: "Can you integrate with banking APIs?", a: "Yes. We integrate with banking APIs, payment gateways and financial data providers using secure protocols." },
      { q: "How do you handle transaction security?", a: "We implement end-to-end encryption, tokenization, two-factor authentication, rate limiting and real-time monitoring." },
    ],
    caseStudy: {
      title: "Payment Processing Platform",
      desc: "Built a real-time payment platform processing thousands of transactions daily for a financial services company.",
      tech: ["Laravel", "React", "PostgreSQL", "AWS", "Stripe"],
      results: "99.99% uptime, 3x faster processing",
    },
  },
  {
    slug: "ecommerce",
    name: "E-commerce",
    tagline: "High-performance commerce experiences.",
    desc: "Custom e-commerce platforms with payment integration, inventory management and analytics for growing businesses.",
    image: "https://readdy.ai/api/search-image?query=Modern%20e-commerce%20platform%20interface%2C%20product%20grid%20with%20shopping%20cart%2C%20clean%20minimal%20design%2C%20blue%20and%20orange%20accent%20lighting%2C%20professional%20online%20store%20UI%20mockup%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-ecommerce&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20e-commerce%20technology%20visualization%2C%20floating%20product%20cards%20and%20shopping%20interfaces%2C%20digital%20commerce%20network%2C%20blue%20and%20orange%20gradient%20lighting%2C%20premium%20technology%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-ecommerce&orientation=landscape",
    icon: "ri-shopping-bag-2-line",
    color: "#f97316",
    challenges: [
      "Scalable product catalogs",
      "Payment gateway integration",
      "Order management at scale",
      "Inventory synchronization",
      "Multi-vendor support",
    ],
    solutions: [
      { title: "Custom E-commerce", desc: "Tailored online stores with unique branding and features.", icon: "ri-store-2-line" },
      { title: "Payment Integration", desc: "Stripe, PayPal, Apple Pay and custom payment solutions.", icon: "ri-bank-card-line" },
      { title: "Inventory Management", desc: "Real-time stock tracking and supplier integration.", icon: "ri-archive-line" },
      { title: "Analytics Dashboard", desc: "Sales, customer and product performance insights.", icon: "ri-pie-chart-line" },
    ],
    features: [
      { title: "Product Catalog", desc: "Unlimited products with variants, categories and filters.", icon: "ri-shopping-bag-line" },
      { title: "Secure Checkout", desc: "PCI-compliant checkout with multiple payment options.", icon: "ri-shield-check-line" },
      { title: "Order Management", desc: "Full order lifecycle from purchase to delivery.", icon: "ri-file-list-line" },
      { title: "Marketing Tools", desc: "Discounts, coupons, email automation and SEO.", icon: "ri-megaphone-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
    ],
    benefits: [
      { title: "Higher Conversion", desc: "Optimized checkout and user experience.", icon: "ri-arrow-up-line" },
      { title: "Scalable Growth", desc: "Handles traffic spikes and product expansion.", icon: "ri-expand-diagonal-line" },
      { title: "Better Analytics", desc: "Data-driven insights for business decisions.", icon: "ri-bar-chart-line" },
      { title: "Lower Costs", desc: "No platform fees, full ownership of your store.", icon: "ri-money-cny-circle-line" },
    ],
    faq: [
      { q: "Can you integrate with payment gateways?", a: "Yes. We integrate Stripe, PayPal, Square, Apple Pay, Google Pay and custom payment solutions." },
      { q: "Can the platform handle thousands of products?", a: "Yes. Our architecture supports unlimited products with efficient search, filtering and pagination." },
      { q: "Do you support multi-vendor marketplaces?", a: "Yes. We build multi-vendor platforms with vendor dashboards, commission management and storefronts." },
    ],
    caseStudy: {
      title: "Multi-Vendor E-commerce Platform",
      desc: "Built a scalable e-commerce platform with 200+ vendors, real-time inventory and multi-payment support.",
      tech: ["Laravel", "Vue.js", "MySQL", "AWS", "Stripe"],
      results: "3x revenue growth, 50+ vendors onboarded",
    },
  },
  {
    slug: "education",
    name: "Education",
    tagline: "Smart learning platforms and digital engagement tools.",
    desc: "Custom learning management systems, student portals and educational SaaS platforms for schools and training organizations.",
    image: "https://readdy.ai/api/search-image?query=Modern%20education%20technology%20platform%2C%20learning%20management%20system%20interface%20with%20course%20cards%20and%20student%20dashboard%2C%20blue%20and%20green%20gradient%20lighting%2C%20professional%20EdTech%20software%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-education&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20education%20technology%20visualization%2C%20floating%20books%20and%20digital%20learning%20elements%2C%20knowledge%20network%2C%20blue%20and%20green%20gradient%20lighting%2C%20premium%20EdTech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-education&orientation=landscape",
    icon: "ri-graduation-cap-line",
    color: "#10b981",
    challenges: [
      "Engaging remote learners",
      "Content management at scale",
      "Progress tracking and analytics",
      "Multi-role access (students, teachers, admins)",
      "Assessment and grading systems",
    ],
    solutions: [
      { title: "Learning Management Systems", desc: "Custom LMS with courses, quizzes and certificates.", icon: "ri-book-open-line" },
      { title: "Student Portals", desc: "Personalized dashboards for progress and resources.", icon: "ri-user-line" },
      { title: "Assessment Tools", desc: "Quizzes, assignments and automated grading.", icon: "ri-questionnaire-line" },
      { title: "Subscription Platforms", desc: "Paid courses with billing and subscription management.", icon: "ri-vip-crown-line" },
    ],
    features: [
      { title: "Course Builder", desc: "Create structured courses with videos, quizzes and files.", icon: "ri-video-line" },
      { title: "Progress Tracking", desc: "Real-time analytics on student engagement and completion.", icon: "ri-bar-chart-line" },
      { title: "Certificates", desc: "Automated certificate generation and verification.", icon: "ri-award-line" },
      { title: "Mobile Ready", desc: "Responsive design and mobile app integration.", icon: "ri-smartphone-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg" },
    ],
    benefits: [
      { title: "Better Engagement", desc: "Interactive content improves student participation.", icon: "ri-user-smile-line" },
      { title: "Scalable Delivery", desc: "Serve thousands of students simultaneously.", icon: "ri-expand-diagonal-line" },
      { title: "Data Insights", desc: "Track learning outcomes and improve content.", icon: "ri-bar-chart-line" },
      { title: "Revenue Growth", desc: "Monetize courses with subscriptions and one-time payments.", icon: "ri-money-cny-circle-line" },
    ],
    faq: [
      { q: "Can students access courses on mobile?", a: "Yes. Our platforms are fully responsive and we also build companion mobile apps using Flutter." },
      { q: "Do you support live classes?", a: "Yes. We can integrate live streaming, video conferencing and interactive whiteboards." },
      { q: "Can the system generate certificates?", a: "Yes. We build automated certificate generation with verification, QR codes and digital signatures." },
    ],
    caseStudy: {
      title: "Online Learning Platform",
      desc: "Built a comprehensive LMS with video courses, quizzes, certificates and a Flutter mobile app for an education startup.",
      tech: ["Laravel", "Vue.js", "Flutter", "MySQL", "AWS"],
      results: "10,000+ students, 95% completion rate",
    },
  },
  {
    slug: "real-estate",
    name: "Real Estate",
    tagline: "Property tech solutions and intelligent market analytics.",
    desc: "Property listing platforms, CRM systems and lead management tools for real estate agencies and property developers.",
    image: "https://readdy.ai/api/search-image?query=Modern%20real%20estate%20platform%20interface%2C%20property%20listings%20with%20maps%20and%20filters%2C%20clean%20dashboard%20design%2C%20blue%20and%20teal%20gradient%20lighting%2C%20professional%20property%20management%20software%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-realestate&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20real%20estate%20technology%20visualization%2C%20floating%20property%20cards%20and%20building%20silhouettes%2C%20digital%20property%20network%2C%20blue%20and%20teal%20gradient%20lighting%2C%20premium%20proptech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-realestate&orientation=landscape",
    icon: "ri-building-line",
    color: "#06b6d4",
    challenges: [
      "Property listing management",
      "Lead tracking and nurturing",
      "Agent portal and commissions",
      "Map integration and search",
      "Document management",
    ],
    solutions: [
      { title: "Property Listings", desc: "Advanced search, filters and map integration.", icon: "ri-map-pin-line" },
      { title: "Real Estate CRM", desc: "Lead tracking, pipeline management and automation.", icon: "ri-customer-service-line" },
      { title: "Agent Portals", desc: "Dashboards for agents to manage listings and clients.", icon: "ri-user-line" },
      { title: "Analytics", desc: "Market trends, pricing insights and performance reports.", icon: "ri-pie-chart-line" },
    ],
    features: [
      { title: "Advanced Search", desc: "Filters by location, price, amenities and more.", icon: "ri-search-line" },
      { title: "Map Integration", desc: "Google Maps with property markers and neighborhood data.", icon: "ri-map-2-line" },
      { title: "Document Vault", desc: "Secure storage for contracts, deeds and legal documents.", icon: "ri-folder-3-line" },
      { title: "Automated Alerts", desc: "Email and SMS notifications for new listings and inquiries.", icon: "ri-notification-3-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
    ],
    benefits: [
      { title: "More Leads", desc: "Advanced search and marketing tools attract buyers.", icon: "ri-user-add-line" },
      { title: "Better Organization", desc: "CRM keeps leads, listings and documents organized.", icon: "ri-folders-line" },
      { title: "Faster Closings", desc: "Automation speeds up the sales pipeline.", icon: "ri-flashlight-line" },
      { title: "Market Insights", desc: "Analytics help agents and developers make data-driven decisions.", icon: "ri-bar-chart-line" },
    ],
    faq: [
      { q: "Can you integrate with MLS systems?", a: "Yes. We integrate with MLS APIs, property data feeds and third-party listing platforms." },
      { q: "Do you support rental management?", a: "Yes. Our platforms support property sales, rentals, lease management and tenant portals." },
      { q: "Is the platform mobile-friendly?", a: "Yes. All platforms are fully responsive and we build mobile apps for agents and clients." },
    ],
    caseStudy: {
      title: "Property Management Platform",
      desc: "Built a real estate CRM with property listings, agent portals and analytics for a regional agency.",
      tech: ["Laravel", "React", "MySQL", "AWS", "Google Maps API"],
      results: "3x lead conversion, 50% faster closings",
    },
  },
  {
    slug: "logistics",
    name: "Logistics",
    tagline: "AI-driven route optimization and supply chain tracking.",
    desc: "Fleet management systems, delivery tracking platforms and supply chain automation for logistics companies.",
    image: "https://readdy.ai/api/search-image?query=Modern%20logistics%20dashboard%2C%20fleet%20tracking%20map%20with%20route%20optimization%2C%20delivery%20status%20panels%2C%20blue%20and%20orange%20gradient%20lighting%2C%20professional%20supply%20chain%20software%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-logistics&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20logistics%20technology%20visualization%2C%20connected%20nodes%20and%20delivery%20routes%2C%20supply%20chain%20network%2C%20blue%20and%20orange%20gradient%20lighting%2C%20premium%20logistics%20tech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-logistics&orientation=landscape",
    icon: "ri-truck-line",
    color: "#f59e0b",
    challenges: [
      "Real-time fleet tracking",
      "Route optimization",
      "Delivery status updates",
      "Warehouse integration",
      "Driver management",
    ],
    solutions: [
      { title: "Fleet Management", desc: "Real-time GPS tracking and vehicle monitoring.", icon: "ri-map-pin-line" },
      { title: "Route Optimization", desc: "AI-powered route planning for efficiency.", icon: "ri-route-line" },
      { title: "Delivery Tracking", desc: "Live delivery status for customers and dispatch.", icon: "ri-truck-line" },
      { title: "Warehouse Integration", desc: "Sync with WMS for inventory and dispatch.", icon: "ri-archive-line" },
    ],
    features: [
      { title: "GPS Tracking", desc: "Real-time location and geofencing alerts.", icon: "ri-map-2-line" },
      { title: "Route Planning", desc: "AI-optimized routes considering traffic and delivery windows.", icon: "ri-route-line" },
      { title: "Proof of Delivery", desc: "Digital signatures, photos and barcode scanning.", icon: "ri-checkbox-circle-line" },
      { title: "Analytics", desc: "Fleet performance, delivery times and cost analysis.", icon: "ri-bar-chart-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg" },
    ],
    benefits: [
      { title: "Faster Deliveries", desc: "Optimized routes reduce delivery time by up to 30%.", icon: "ri-flashlight-line" },
      { title: "Lower Costs", desc: "Fuel savings and reduced fleet maintenance.", icon: "ri-money-cny-circle-line" },
      { title: "Better Visibility", desc: "Real-time tracking for customers and dispatch.", icon: "ri-eye-line" },
      { title: "Scalable Operations", desc: "Handle growing fleet and delivery volume.", icon: "ri-expand-diagonal-line" },
    ],
    faq: [
      { q: "Can drivers use a mobile app?", a: "Yes. We build Flutter mobile apps for drivers with GPS, route navigation and proof of delivery." },
      { q: "Does it support third-party GPS hardware?", a: "Yes. We integrate with GPS trackers, OBD devices and fleet management hardware." },
      { q: "Can customers track their deliveries?", a: "Yes. We provide customer-facing tracking with real-time updates and ETA notifications." },
    ],
    caseStudy: {
      title: "Fleet Management Platform",
      desc: "Built a fleet tracking and delivery management system with mobile apps for drivers and customers.",
      tech: ["Laravel", "React", "Flutter", "PostgreSQL", "AWS"],
      results: "30% faster deliveries, 25% fuel savings",
    },
  },
  {
    slug: "travel",
    name: "Travel",
    tagline: "Intelligent booking systems and personalized travel experiences.",
    desc: "Travel booking platforms, itinerary management and partner integration for travel agencies and tourism businesses.",
    image: "https://readdy.ai/api/search-image?query=Modern%20travel%20booking%20platform%20interface%2C%20hotel%20and%20flight%20search%20with%20beautiful%20imagery%2C%20clean%20minimal%20design%2C%20blue%20and%20teal%20gradient%20lighting%2C%20professional%20travel%20software%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-travel&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20travel%20technology%20visualization%2C%20floating%20destination%20cards%20and%20flight%20paths%2C%20digital%20travel%20network%2C%20blue%20and%20teal%20gradient%20lighting%2C%20premium%20travel%20tech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-travel&orientation=landscape",
    icon: "ri-plane-line",
    color: "#0ea5e9",
    challenges: [
      "Multi-provider integration",
      "Real-time pricing and availability",
      "Booking management across channels",
      "Payment in multiple currencies",
      "Itinerary coordination",
    ],
    solutions: [
      { title: "Booking Platforms", desc: "Hotels, flights, cars and activities in one platform.", icon: "ri-ticket-line" },
      { title: "Itinerary Management", desc: "Day-by-day trip planning and coordination tools.", icon: "ri-calendar-line" },
      { title: "Partner APIs", desc: "Integration with GDS, OTAs and local providers.", icon: "ri-exchange-line" },
      { title: "Customer Management", desc: "CRM for traveler profiles, preferences and history.", icon: "ri-user-line" },
    ],
    features: [
      { title: "Smart Search", desc: "AI-powered recommendations based on preferences.", icon: "ri-search-line" },
      { title: "Multi-currency", desc: "Pricing and payments in multiple currencies.", icon: "ri-coins-line" },
      { title: "Mobile App", desc: "Flutter app for bookings, check-ins and itineraries.", icon: "ri-smartphone-line" },
      { title: "Reviews", desc: "Rating and review system for properties and experiences.", icon: "ri-star-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg" },
    ],
    benefits: [
      { title: "More Bookings", desc: "Streamlined search and checkout increase conversions.", icon: "ri-arrow-up-line" },
      { title: "Better Operations", desc: "Automated booking management reduces manual work.", icon: "ri-settings-3-line" },
      { title: "Customer Loyalty", desc: "Personalized experiences drive repeat bookings.", icon: "ri-heart-3-line" },
      { title: "Global Reach", desc: "Multi-language and multi-currency for international travelers.", icon: "ri-global-line" },
    ],
    faq: [
      { q: "Can you integrate with booking APIs?", a: "Yes. We integrate with GDS, hotel APIs, flight APIs and local tour provider systems." },
      { q: "Do you support multiple currencies?", a: "Yes. Our platforms support multi-currency pricing, dynamic exchange rates and localized payments." },
      { q: "Is there a mobile app for travelers?", a: "Yes. We build Flutter companion apps for travelers with bookings, itineraries and notifications." },
    ],
    caseStudy: {
      title: "Travel Booking Platform",
      desc: "Built a comprehensive travel platform with hotel booking, flight search and itinerary management.",
      tech: ["Laravel", "Vue.js", "Flutter", "MySQL", "AWS"],
      results: "2x booking growth, 90% customer satisfaction",
    },
  },
  {
    slug: "saas",
    name: "SaaS",
    tagline: "Scalable software products built for growth.",
    desc: "Multi-tenant SaaS platforms with subscriptions, user management and analytics for product companies and startups.",
    image: "https://readdy.ai/api/search-image?query=Modern%20SaaS%20dashboard%20interface%2C%20multi-tenant%20admin%20panel%20with%20user%20analytics%2C%20clean%20minimal%20design%2C%20blue%20and%20purple%20gradient%20lighting%2C%20professional%20SaaS%20software%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-saas&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20SaaS%20technology%20visualization%2C%20floating%20dashboard%20cards%20and%20subscription%20metrics%2C%20cloud%20architecture%20nodes%2C%20blue%20and%20purple%20gradient%20lighting%2C%20premium%20SaaS%20tech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-saas&orientation=landscape",
    icon: "ri-cloud-line",
    color: "#6366f1",
    challenges: [
      "Multi-tenant architecture",
      "Subscription billing complexity",
      "User roles and permissions",
      "API scalability",
      "White-label customization",
    ],
    solutions: [
      { title: "SaaS Architecture", desc: "Multi-tenant systems with data isolation and scalability.", icon: "ri-building-line" },
      { title: "Subscription Management", desc: "Billing, trials, upgrades and usage tracking.", icon: "ri-vip-crown-line" },
      { title: "Admin Dashboards", desc: "Powerful dashboards for tenants and super-admins.", icon: "ri-dashboard-line" },
      { title: "API Platform", desc: "Developer APIs for integrations and extensions.", icon: "ri-exchange-line" },
    ],
    features: [
      { title: "Multi-tenancy", desc: "Isolated data, custom domains and branding per tenant.", icon: "ri-building-4-line" },
      { title: "Billing Engine", desc: "Stripe, PayPal, ACH and custom billing cycles.", icon: "ri-bank-card-line" },
      { title: "User Roles", desc: "Granular permissions, teams and workspaces.", icon: "ri-team-line" },
      { title: "Analytics", desc: "Usage metrics, churn analysis and revenue reports.", icon: "ri-bar-chart-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
    ],
    benefits: [
      { title: "Faster Time to Market", desc: "Launch your SaaS in weeks, not months.", icon: "ri-rocket-line" },
      { title: "Scalable Architecture", desc: "Grows with your user base without re-architecture.", icon: "ri-expand-diagonal-line" },
      { title: "Revenue Growth", desc: "Built-in billing and subscription management.", icon: "ri-money-cny-circle-line" },
      { title: "Customer Retention", desc: "Analytics help you understand and retain users.", icon: "ri-heart-3-line" },
    ],
    faq: [
      { q: "Can you build a white-label SaaS?", a: "Yes. We build multi-tenant platforms with custom branding, domains and configurations per tenant." },
      { q: "How do you handle billing?", a: "We integrate Stripe, PayPal and custom billing with trials, proration, usage-based pricing and invoicing." },
      { q: "Is the API documented?", a: "Yes. We provide comprehensive API documentation using Swagger/OpenAPI with code examples and SDKs." },
    ],
    caseStudy: {
      title: "Multi-Tenant SaaS Platform",
      desc: "Built a white-label SaaS platform with 500+ tenants, subscription billing and analytics dashboard.",
      tech: ["Laravel", "React", "PostgreSQL", "AWS", "Stripe"],
      results: "500+ tenants, 40% MoM growth",
    },
  },
  {
    slug: "enterprise",
    name: "Enterprise",
    tagline: "AI workflow automation, business intelligence and custom ERP.",
    desc: "Enterprise-grade applications, ERP systems and workflow automation for large organizations and government agencies.",
    image: "https://readdy.ai/api/search-image?query=Modern%20enterprise%20software%20dashboard%2C%20workflow%20automation%20interface%20with%20process%20charts%2C%20clean%20professional%20design%2C%20blue%20and%20navy%20gradient%20lighting%2C%20premium%20enterprise%20software%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-enterprise&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20enterprise%20technology%20visualization%2C%20connected%20workflow%20nodes%20and%20data%20pipelines%2C%20enterprise%20architecture%2C%20blue%20and%20navy%20gradient%20lighting%2C%20premium%20corporate%20tech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-enterprise&orientation=landscape",
    icon: "ri-building-4-line",
    color: "#1e40af",
    challenges: [
      "Legacy system integration",
      "Complex workflow automation",
      "Role-based access across departments",
      "Data security and compliance",
      "Scalability for thousands of users",
    ],
    solutions: [
      { title: "Enterprise Applications", desc: "Custom ERP, CRM and workflow management systems.", icon: "ri-building-4-line" },
      { title: "Workflow Automation", desc: "Automate complex business processes and approvals.", icon: "ri-settings-3-line" },
      { title: "API Ecosystems", desc: "Connect legacy systems with modern APIs.", icon: "ri-exchange-line" },
      { title: "Business Intelligence", desc: "Dashboards and reports for executive decision-making.", icon: "ri-bar-chart-line" },
    ],
    features: [
      { title: "Single Sign-On", desc: "SAML, OAuth and LDAP integration.", icon: "ri-shield-check-line" },
      { title: "Audit Logging", desc: "Complete audit trails for compliance and security.", icon: "ri-file-list-line" },
      { title: "Workflow Builder", desc: "Visual workflow designer with conditional logic.", icon: "ri-flow-chart" },
      { title: "Reporting", desc: "Custom reports, exports and scheduled delivery.", icon: "ri-pie-chart-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
    ],
    benefits: [
      { title: "Process Efficiency", desc: "Automation reduces manual work and errors.", icon: "ri-settings-3-line" },
      { title: "Data Security", desc: "Enterprise-grade security and compliance.", icon: "ri-shield-check-line" },
      { title: "Scalability", desc: "Supports thousands of users across departments.", icon: "ri-expand-diagonal-line" },
      { title: "Integration", desc: "Connects with existing systems and tools.", icon: "ri-links-line" },
    ],
    faq: [
      { q: "Can you integrate with our existing systems?", a: "Yes. We build APIs and connectors for SAP, Salesforce, Microsoft Dynamics and custom legacy systems." },
      { q: "Do you support SSO and LDAP?", a: "Yes. We implement SAML 2.0, OAuth 2.0, OpenID Connect and LDAP/Active Directory integration." },
      { q: "How do you handle compliance?", a: "We follow SOC 2, ISO 27001 and GDPR compliance practices with audit logging and data governance." },
    ],
    caseStudy: {
      title: "Enterprise Workflow Platform",
      desc: "Built a workflow automation platform connecting 12 departments for a Fortune 500 company.",
      tech: ["Laravel", "React", "PostgreSQL", "AWS", "Docker"],
      results: "60% process efficiency gain, 5,000+ users",
    },
  },
  {
    slug: "media-entertainment",
    name: "Media & Entertainment",
    tagline: "AI content generation, streaming platforms and digital experiences.",
    desc: "Content management systems, streaming platforms and digital media experiences for entertainment companies and creators.",
    image: "https://readdy.ai/api/search-image?query=Modern%20media%20streaming%20platform%20interface%2C%20video%20content%20grid%20with%20player%2C%20clean%20minimal%20design%2C%20dark%20theme%20with%20purple%20and%20pink%20accents%2C%20professional%20media%20software%2C%20no%20text%2C%20no%20people&width=600&height=400&seq=ind-media&orientation=landscape",
    heroImage: "https://readdy.ai/api/search-image?query=Abstract%20media%20technology%20visualization%2C%20floating%20video%20cards%20and%20content%20streams%2C%20digital%20entertainment%20network%2C%20purple%20and%20pink%20gradient%20lighting%2C%20premium%20media%20tech%20aesthetic%2C%20dark%20background%2C%20no%20text%2C%20no%20people&width=1400&height=800&seq=ind-hero-media&orientation=landscape",
    icon: "ri-movie-line",
    color: "#d946ef",
    challenges: [
      "Content delivery at scale",
      "Video streaming infrastructure",
      "Digital rights management",
      "User engagement analytics",
      "Monetization models",
    ],
    solutions: [
      { title: "Content Platforms", desc: "CMS for articles, videos and podcasts.", icon: "ri-article-line" },
      { title: "Streaming Systems", desc: "Video and audio streaming with adaptive bitrate.", icon: "ri-movie-line" },
      { title: "Subscription Platforms", desc: "Paywall, subscriptions and tiered access.", icon: "ri-vip-crown-line" },
      { title: "Analytics", desc: "Engagement metrics, watch time and content performance.", icon: "ri-bar-chart-line" },
    ],
    features: [
      { title: "Media Management", desc: "Upload, transcode and organize video and audio.", icon: "ri-folders-line" },
      { title: "Adaptive Streaming", desc: "HLS and DASH for smooth playback on any device.", icon: "ri-movie-line" },
      { title: "DRM", desc: "Digital rights management and content protection.", icon: "ri-shield-check-line" },
      { title: "Monetization", desc: "Subscriptions, ads, pay-per-view and donations.", icon: "ri-money-cny-circle-line" },
    ],
    techStack: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg" },
    ],
    benefits: [
      { title: "Global Reach", desc: "CDN delivery for audiences worldwide.", icon: "ri-global-line" },
      { title: "Revenue Growth", desc: "Multiple monetization models built in.", icon: "ri-money-cny-circle-line" },
      { title: "User Engagement", desc: "Analytics help you create better content.", icon: "ri-user-smile-line" },
      { title: "Scalable Delivery", desc: "Handle millions of views without downtime.", icon: "ri-expand-diagonal-line" },
    ],
    faq: [
      { q: "Can you handle video streaming?", a: "Yes. We implement HLS/DASH streaming with adaptive bitrate, CDN delivery and DRM protection." },
      { q: "Do you support subscription models?", a: "Yes. We build tiered subscriptions, free trials, pay-per-view and ad-supported models." },
      { q: "Is the platform mobile-friendly?", a: "Yes. We build responsive web platforms and Flutter mobile apps for content consumption." },
    ],
    caseStudy: {
      title: "Streaming Content Platform",
      desc: "Built a video streaming platform with subscriptions, analytics and mobile apps for a media company.",
      tech: ["Laravel", "React", "Flutter", "MySQL", "AWS"],
      results: "1M+ streams, 85% subscriber retention",
    },
  },
];